import { AxiosProgressEvent, AxiosResponse } from 'axios';

import {
  CreateFileRequestType,
  CreateScreenRequestType,
  CreateScreenResponseType,
  EditFileRequestType,
  AbortVideoFileRequestType,
  EndVideoFileRequestType,
  PartDownloadResponseType,
  PartVideoFileRequestType,
  PresignPartRequestType,
  PresignPartResponseType,
  StartDownloadResponseType,
  StartVideoFileRequestType,
} from './types';

import { CommonResponseType } from 'api/authApi';
import { GeneralResponseType } from 'common';
import { authServiceClient } from 'config';
import { FileType } from 'state';

export const fetchFilesRequest = (
  params: { inspectionId: string; take?: number },
  silent?: boolean,
) => {
  return authServiceClient.get<
    GeneralResponseType<FileType>,
    AxiosResponse<GeneralResponseType<FileType>>,
    { inspectionId: string }
  >(`/inspection-file`, { params, silentError: silent });
};

export const createFileRequest = (
  data: CreateFileRequestType,
  callback: (percentCompleted: number, name: string) => void,
  signal: AbortSignal,
) => {
  const config = {
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      const percentCompleted = progressEvent.total
        ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
        : 0;

      callback(percentCompleted, data.file.name);
    },
    headers: { 'Content-Type': 'multipart/form-data' },
    signal,
  };

  return authServiceClient.post<FileType, AxiosResponse<FileType>, CreateFileRequestType>(
    `/inspection-file`,
    data,
    config,
  );
};

export const startDownloadVideoFileRequest = (data: StartVideoFileRequestType) => {
  return authServiceClient.post<
    StartDownloadResponseType,
    AxiosResponse<StartDownloadResponseType>
  >(`/inspection-file/multipart/video/start`, data);
};

export const partDownloadVideoFileRequest = (data: PartVideoFileRequestType) => {
  return authServiceClient.post<
    PartDownloadResponseType,
    AxiosResponse<PartDownloadResponseType>
  >(`/inspection-file/multipart/video/upload`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const presignPartRequest = (
  data: PresignPartRequestType,
  signal?: AbortSignal,
) => {
  return authServiceClient.post<
    PresignPartResponseType,
    AxiosResponse<PresignPartResponseType>
  >(`/inspection-file/multipart/video/presign`, data, { signal });
};

export const uploadPartDirectlyToS3 = async (
  presignedUrl: string,
  chunk: Blob,
): Promise<void> => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: chunk,
  });

  if (!response.ok) {
    throw new Error(`S3 part upload failed with status ${response.status}`);
  }
};

let uploadWorker: Worker | null = null;
const pendingUploads = new Map<
  number,
  { resolve: (etag: string) => void; reject: (e: Error) => void }
>();
let nextUploadId = 0;

const getUploadWorker = (): Worker => {
  if (!uploadWorker) {
    uploadWorker = new Worker(new URL('./upload.worker.ts', import.meta.url));
    uploadWorker.onmessage = (e: MessageEvent) => {
      const { id, success, error, etag } = e.data;
      const cb = pendingUploads.get(id);

      if (!cb) return;

      pendingUploads.delete(id);

      if (success) {
        cb.resolve(etag ?? '');
      } else {
        cb.reject(new Error(error ?? 'Worker upload failed'));
      }
    };
    uploadWorker.onerror = () => {
      pendingUploads.forEach(cb => cb.reject(new Error('Upload worker crashed')));
      pendingUploads.clear();
      uploadWorker = null;
    };
  }

  return uploadWorker;
};

export const uploadPartViaWorker = async (
  presignedUrl: string,
  chunk: Blob,
  signal?: AbortSignal,
): Promise<string> => {
  if (signal?.aborted) {
    throw new Error('Upload aborted');
  }

  const buffer = await chunk.arrayBuffer();

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Upload aborted'));

      return;
    }

    const id = nextUploadId;

    nextUploadId += 1;
    pendingUploads.set(id, { resolve, reject });

    const onAbort = () => {
      pendingUploads.delete(id);
      reject(new Error('Upload aborted'));
    };

    signal?.addEventListener('abort', onAbort, { once: true });
    getUploadWorker().postMessage({ id, presignedUrl, buffer }, [buffer]);
  });
};

export const uploadWithSlidingWindow = async (
  totalParts: number,
  concurrency: number,
  uploadPart: (partNumber: number) => Promise<string>,
  onProgress: (completedParts: number, total: number) => void,
  abortSignal: AbortSignal,
): Promise<{ ETag: string; PartNumber: number }[]> => {
  let nextPart = 1;
  let completed = 0;
  const results: { ETag: string; PartNumber: number }[] = [];

  const runSlot = async (): Promise<void> => {
    while (nextPart <= totalParts) {
      if (abortSignal.aborted) {
        throw new Error('Upload aborted');
      }

      const partNumber = nextPart;

      nextPart += 1;
      // eslint-disable-next-line no-await-in-loop
      const etag = await uploadPart(partNumber);

      results.push({ ETag: etag, PartNumber: partNumber });
      completed += 1;
      onProgress(completed, totalParts);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, totalParts) }, runSlot));

  return results.sort((a, b) => a.PartNumber - b.PartNumber);
};

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  signal: AbortSignal | undefined,
  maxRetries = 3,
  baseDelayMs = 1000,
  maxDelayMs = Infinity,
): Promise<T> => {
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (signal?.aborted) throw new Error('Upload aborted');
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      if (signal?.aborted) throw new Error('Upload aborted');
      if (attempt === maxRetries) throw err;
      // eslint-disable-next-line no-await-in-loop
      await sleep(Math.min(baseDelayMs * 2 ** attempt, maxDelayMs));
    }
  }
  throw new Error('retryWithBackoff: unreachable');
};

export const endDownloadVideoFileRequest = (data: EndVideoFileRequestType) => {
  return authServiceClient.post<any, AxiosResponse<any>>(
    `/inspection-file/multipart/video/complete`,
    data,
  );
};

// Aborts an in-progress S3 multipart upload so a failed/cancelled upload does not
// leave an orphaned multipart on the bucket.
// Depends on backend endpoint `POST /inspection-file/multipart/video/abort`.
export const abortVideoUploadRequest = (data: AbortVideoFileRequestType) => {
  return authServiceClient.post<
    CommonResponseType,
    AxiosResponse<CommonResponseType>,
    AbortVideoFileRequestType
  >(`/inspection-file/multipart/video/abort`, data);
};

export const editFileRequest = (file: EditFileRequestType & { id: string }) => {
  return authServiceClient.put<FileType, AxiosResponse<FileType>, EditFileRequestType>(
    `/inspection-file/${file.id}`,
    file,
  );
};

export const deleteFileRequest = (id: string) => {
  return authServiceClient.delete<CommonResponseType, AxiosResponse<CommonResponseType>>(
    `/inspection-file/${id}`,
  );
};

export const deleteFilesRequest = (ids: string[]) => {
  return authServiceClient.delete<CommonResponseType, AxiosResponse<CommonResponseType>>(
    '/inspection-file/delete-all',
    { data: ids },
  );
};

export const createScreenRequest = (data: CreateScreenRequestType) => {
  return authServiceClient.post<
    CreateScreenResponseType,
    AxiosResponse<CreateScreenResponseType>
  >(`/screenshot`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
